// Esqueleto de carga (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.9): bloques
// con la forma de lo que va a aparecer y un brillo que recorre (styles.css,
// `.hueso`); con prefers-reduced-motion queda el bloque fijo. Es decorativo:
// el "cargando" lo anuncia el contenedor con aria-busy y un texto para el
// lector de pantalla, no estos bloques.
import { cn } from "./cn.js";

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div aria-hidden="true" className={cn("hueso h-3", className)} {...props} />;
}

/** Filas de una lista de viajes: hora, dos lineas de texto, plazo y botones. */
export function SkeletonRows({ rows = 3, label = "Cargando" }: { rows?: number; label?: string }): React.JSX.Element {
  return (
    <div aria-busy="true" className="flex flex-col">
      <p role="status" className="sr-only">
        {label}...
      </p>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} aria-hidden="true" className="grid grid-cols-[56px_1fr] items-center gap-x-3.5 gap-y-2 border-t border-divisor px-3.5 py-3 first:border-t-0 md:grid-cols-[64px_minmax(0,1fr)_90px_150px]">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-11" />
            <Skeleton className="h-2 w-8" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Skeleton className="w-3/4" />
            <Skeleton className="h-2.5 w-1/2" />
          </div>
          <Skeleton className="h-4 w-14 justify-self-end" />
          <div className="flex gap-1.5">
            <Skeleton className="h-[26px] w-16" />
            <Skeleton className="h-[26px] w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Filas de una tabla densa: `cols` celdas de ancho variable por fila. */
export function SkeletonTable({ rows = 4, cols = 5, label = "Cargando" }: { rows?: number; cols?: number; label?: string }): React.JSX.Element {
  return (
    <div aria-busy="true" className="flex flex-col py-1">
      <p role="status" className="sr-only">
        {label}...
      </p>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} aria-hidden="true" className="grid gap-3.5 border-t border-divisor px-3.5 py-2.5 first:border-t-0" style={{ gridTemplateColumns: `repeat(${String(cols)}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} style={{ width: `${String(c === 0 ? 60 : 40 + ((c * 17 + r * 7) % 40))}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** La franja de KPIs mientras carga: cuatro celdas con rotulo, valor y detalle. */
export function SkeletonKpis({ cells = 4 }: { cells?: number }): React.JSX.Element {
  return (
    <div aria-busy="true" className="grid grid-cols-2 rounded-lg border border-linea bg-superficie md:grid-flow-col md:auto-cols-fr">
      <p role="status" className="sr-only">
        Cargando el resumen...
      </p>
      {Array.from({ length: cells }, (_, i) => (
        <div key={i} aria-hidden="true" className="flex flex-col gap-2 border-l border-divisor px-4 py-2.5 first:border-l-0">
          <Skeleton className="h-2 w-3/5" />
          <Skeleton className="h-5 w-8" />
          <Skeleton className="h-2 w-4/5" />
        </div>
      ))}
    </div>
  );
}
